import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { PROXY_JOBS, ProxyJob, ProxyFollowUpRecord } from '../data/mockProxyJobs';
import type { ProxyWorkflowType } from '../data/proxyWorkflowTypes';

// Single shared source of truth for all mutable PROXY (ICT/Collections) job
// state — every screen (Follow-Up Queue, Shortages, Contests, RDQ Calculator,
// Inbox & Triage, Ace, the Governance Dashboard tab) reads and writes through
// here so a mutation in one screen (e.g. sending a follow-up) is immediately
// visible everywhere else.
//
// Persists only an overlay DIFF on top of the immutable PROXY_JOBS array (not
// full mutated records) — mirrors ChatsContext.tsx's localStorage idiom, but
// diff-based so edits to the underlying mock dataset never go stale against a
// full persisted copy.

// One record per approved task, listing what the person changed in Ace's
// review card before approving (empty = approved exactly as Ace drafted it).
// Feeds Proxy Governance's "approved as drafted" rate.
export interface ProxyApproval {
  taskId: string;
  workflowType: ProxyWorkflowType;
  jobId: string;
  title: string;
  at: string; // "YYYY-MM-DD HH:MM"
  by: string;
  edits: string[]; // field labels, or 'Recipient' / 'CC' / 'Subject' / 'Email body'
  // The ticket (case) this step belongs to, and whether it finished it.
  ticketKey?: string;
  closesTicket?: boolean;
}

// Approvals from the two working days before the demo's "today", so the
// dashboard reads like a team that has been using Ace for a while.
const SEED_APPROVALS: ProxyApproval[] = [
  { taskId: 'seed-a01', workflowType: 'MATERIAL_PDF_ROUTE', jobId: '3021919', title: 'Powell Industries Inc - Material PDF Routing', at: '2026-05-28 08:41', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a02', workflowType: 'MATERIAL_FOLLOWUP', jobId: '9100014', title: 'Brookfield Paper Co: material follow-up', at: '2026-05-28 09:05', by: 'ICT Associate', edits: ['Email body'] },
  { taskId: 'seed-a03', workflowType: 'RDQ_ORDER', jobId: '9100002', title: 'Redstone Materials Co - RDQ recalculation', at: '2026-05-28 09:30', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a04', workflowType: 'JOB_CHANGE_ROUTE', jobId: '9100001', title: 'Cascade Timber Holdings - Job Change Routing', at: '2026-05-28 10:12', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a05', workflowType: 'MATERIAL_FOLLOWUP', jobId: '9100041', title: 'Copperfield Energy: material follow-up', at: '2026-05-28 10:48', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a06', workflowType: 'CREDIT_HOLD_ROUTE', jobId: '9100040', title: 'Ashford Retail Partners - Credit Hold Routing', at: '2026-05-28 11:20', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a07', workflowType: 'RDQ_ORDER', jobId: '9100022', title: 'Granite Peak Mining - RDQ recalculation', at: '2026-05-28 14:02', by: 'ICT Associate', edits: ['Active Full Set'] },
  { taskId: 'seed-a08', workflowType: 'BILLING_VERIFICATION_ROUTE', jobId: '9100071', title: 'Prairie Wind Energy Partners - Billing Verification Routing', at: '2026-05-28 15:16', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a09', workflowType: 'MATERIAL_FOLLOWUP', jobId: '9100045', title: 'Talisman Robotics Corp: material follow-up', at: '2026-05-29 08:37', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a10', workflowType: 'MATERIAL_PDF_ROUTE', jobId: '3025905', title: 'Laurentian Bank of Canada - Material PDF Routing', at: '2026-05-29 09:10', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a11', workflowType: 'PROXY_CONTEST', jobId: '9200001', title: 'Kessler Foods International - Proxy contest', at: '2026-05-29 09:54', by: 'ICT Associate', edits: ['CC', 'Email body'] },
  { taskId: 'seed-a12', workflowType: 'MATERIAL_FOLLOWUP', jobId: '9100062', title: 'Thunder Bay Minerals Corp: material follow-up', at: '2026-05-29 10:31', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a13', workflowType: 'BILLING_VERIFICATION_ROUTE', jobId: '9100023', title: 'Fenwick Data Systems - Billing Verification Routing', at: '2026-05-29 13:45', by: 'ICT Associate', edits: [] },
  { taskId: 'seed-a14', workflowType: 'MATERIAL_FOLLOWUP', jobId: '9100043', title: 'Beacon Hill Software: material follow-up', at: '2026-05-29 14:22', by: 'ICT Associate', edits: ['Recipient'] },
];

interface ProxyDataContextValue {
  jobs: ProxyJob[];
  approvals: ProxyApproval[];
  recordApproval: (approval: ProxyApproval) => void;
  getJob: (jobId: string) => ProxyJob | undefined;
  updateJob: (jobId: string, patch: Partial<ProxyJob>) => void;
  appendFollowUp: (jobId: string, entry: Omit<ProxyFollowUpRecord, 'id'>) => void;
  // Put these jobs back to their demo starting state (the demo guide's Restart).
  resetJobs: (jobIds: string[]) => void;
}

const ProxyDataContext = createContext<ProxyDataContextValue | null>(null);

const LS_KEY = 'opsai.proxyjobs.v1';
const APPROVALS_KEY = 'opsai.proxyapprovals.v1';

function safeParse(json: string | null, fallback: Record<string, Partial<ProxyJob>>) {
  try {
    const v = JSON.parse(json || '');
    return (v && typeof v === 'object') ? v : fallback;
  } catch {
    return fallback;
  }
}

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function writeThrough(next: Record<string, Partial<ProxyJob>>) {
  if (typeof window !== 'undefined') window.localStorage.setItem(LS_KEY, JSON.stringify(next));
}

export function ProxyDataProvider({ children }: { children: ReactNode }) {
  const [overlay, setOverlay] = useState<Record<string, Partial<ProxyJob>>>(() => {
    if (typeof window === 'undefined') return {};
    return safeParse(window.localStorage.getItem(LS_KEY), {});
  });

  const jobs = useMemo(
    () => PROXY_JOBS.map(j => (overlay[j.jobId] ? { ...j, ...overlay[j.jobId] } : j)),
    [overlay],
  );

  const getJob = useCallback((jobId: string) => jobs.find(j => j.jobId === jobId), [jobs]);

  // Shallow merge — callers patching a nested sub-object (contest, shortageLines)
  // must spread the existing sub-object themselves before calling this.
  const updateJob = useCallback((jobId: string, patch: Partial<ProxyJob>) => {
    setOverlay(prevOverlay => {
      const next = { ...prevOverlay, [jobId]: { ...prevOverlay[jobId], ...patch } };
      writeThrough(next);
      return next;
    });
  }, []);

  const appendFollowUp = useCallback((jobId: string, entry: Omit<ProxyFollowUpRecord, 'id'>) => {
    const base = PROXY_JOBS.find(j => j.jobId === jobId);
    if (!base) return;
    setOverlay(prevOverlay => {
      const currentHistory = (prevOverlay[jobId]?.followUpHistory ?? base.followUpHistory) || [];
      const record: ProxyFollowUpRecord = { id: uid(), ...entry };
      const next = {
        ...prevOverlay,
        [jobId]: { ...prevOverlay[jobId], followUpHistory: [...currentHistory, record] },
      };
      writeThrough(next);
      return next;
    });
  }, []);

  const resetJobs = useCallback((jobIds: string[]) => {
    setOverlay(prevOverlay => {
      const next = { ...prevOverlay };
      jobIds.forEach(id => { delete next[id]; });
      writeThrough(next);
      return next;
    });
  }, []);

  const [liveApprovals, setLiveApprovals] = useState<ProxyApproval[]>(() => {
    try { const v = JSON.parse(window.localStorage.getItem(APPROVALS_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
  });
  const approvals = useMemo(() => [...SEED_APPROVALS, ...liveApprovals], [liveApprovals]);

  const recordApproval = useCallback((approval: ProxyApproval) => {
    setLiveApprovals(prev => {
      const next = [...prev, approval];
      try { window.localStorage.setItem(APPROVALS_KEY, JSON.stringify(next)); } catch { /* still works for this session */ }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ jobs, approvals, recordApproval, getJob, updateJob, appendFollowUp, resetJobs }),
    [jobs, approvals, recordApproval, getJob, updateJob, appendFollowUp, resetJobs],
  );

  return <ProxyDataContext.Provider value={value}>{children}</ProxyDataContext.Provider>;
}

export function useProxyData(): ProxyDataContextValue {
  const ctx = useContext(ProxyDataContext);
  if (!ctx) throw new Error('useProxyData must be used inside ProxyDataProvider');
  return ctx;
}
